import {
  useGetPackHousesQuery,
  useGetPickScheduleSummaryQuery,
} from 'common/api/endpoints/schedule';
import { handleError } from 'common/api/utils/handleError';
import { CsvButton } from 'common/components/CsvButton/CsvButton';
import { WithLoadingOverlay } from 'common/components/LoadingSpinner';
import { SwitcherTextStyle } from 'common/components/TimeframeSwitcher/styles';
import { TimeframeSwitcher } from 'common/components/TimeframeSwitcher/TimeframeSwitcher';
import { useTimeframeSwitcherState } from 'common/components/TimeframeSwitcher/useTimeframeSwitcherState';
import useWindowSize from 'common/hooks/useWindowSize';
import { DateOnlyFormat } from 'common/models/dateType';
import { mobile } from 'common/styles/breakpoints';
import { blueShadeHeader, red } from 'common/styles/colors';
import { FormPadding } from 'common/styles/form';
import { boxShadow } from 'common/styles/page';
import moment from 'moment';
import { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { DayInfo } from 'utils/dateTimeHelpers';
import { generateWeekEndString } from 'utils/helperFunction';
import { PackHousePickScheduleTable } from '../components/PackHousePickScheduleTable';
import { ApprovalRow } from '../components/PickSchedule/ApprovalRow';
import { DailyTotalRow } from '../components/PickSchedule/DailyTotalRow';
import { DaySelector } from '../components/PickSchedule/DaySelector';
import { useDaySelector } from '../hooks/useDaySelector';
import { PackHousePickScheduleTableData } from '../hooks/usePackHousePickScheduleTableData';
import {
  formatCsvData,
  pickSchedHeaders,
  PickSchedKeys,
} from '../utils/preparePickSchedCsvData';
import { TitleAndCsvBtn } from './styles';

function weekdaysToRange(week: DayInfo[]) {
  return `${week[0].month}-${week[0].day} to ${week[week.length - 1].month}-${
    week[week.length - 1].day
  }`;
}

const StyledContainer = styled.div`
  box-shadow: ${boxShadow};
  border-radius: 8px;
  padding: ${FormPadding};

  h1 {
    font-family: KanitLight;
    font-size: 32px;
    line-height: 140%;
    letter-spacing: 0.0075em;
    color: ${blueShadeHeader};
  }

  .error {
    color: ${red};
  }

  @media (max-width: ${mobile}) {
    padding: 0;
  }
`;

const Header = styled.div`
  @media (max-width: ${mobile}) {
    padding: 5px;
  }
`;

const WeekRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;

  h3 {
    ${SwitcherTextStyle}
    margin: 0;
    text-wrap: nowrap;
  }

  .mobile-only {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
  }

  @media (max-width: ${mobile}) {
    flex-direction: column;
    align-items: flex-start;

    div {
      display: flex;
      align-items: center;

      h3 {
        margin-right: 10px;
      }
    }
  }
`;

export type PackHousePickSchedule = {
  packHouseCode: string;
  rows: PackHousePickScheduleTableData[];
  totals: {
    scheduledTotal: number;
    pickedTotal: number;
    binsLeftTotal: number;
  };
};

export const PickScheduleView: FC = () => {
  const { width } = useWindowSize();
  const isMobile = width < parseInt(mobile, 10);
  const { state: week } = useTimeframeSwitcherState('pick-schedule', 'week');
  const { offset } = useDaySelector();

  const selectedDate: {
    withYear: string;
    withoutYear: string;
    scheduleDay: string;
  } = useMemo(() => {
    const date = moment(week.monday);

    date.add(offset.value, 'days');

    return {
      withYear: date.format(DateOnlyFormat),
      withoutYear: date.format('MM-DD'),
      scheduleDay: date.format('ddd'),
    };
  }, [offset.value, week.monday]);

  const {
    data: packHouses,
    isLoading,
    error: fetchPHError,
  } = useGetPackHousesQuery();
  const {
    data: summaryData,
    isFetching: isSummaryFetching,
    error: fetchSummaryError,
  } = useGetPickScheduleSummaryQuery(selectedDate.withYear, {
    refetchOnMountOrArgChange: true,
  });
  const [pickSchedules, setPickSchedules] = useState<PackHousePickSchedule[]>(
    [],
  );
  const [csvData, setCsvData] = useState<Record<PickSchedKeys, string>[]>([]);
  const [dataLoadingCount, setDataLoadingCount] = useState(0);

  useEffect(() => {
    if (fetchPHError) handleError(fetchPHError, 'Unable to load pack houses.');
  }, [fetchPHError]);

  useEffect(() => {
    if (fetchSummaryError)
      handleError(fetchSummaryError, 'Unable to load daily total.');
  }, [fetchSummaryError]);

  return (
    <StyledContainer>
      <Header>
        <TitleAndCsvBtn>
          <h1>Pick Schedule</h1>
          <CsvButton
            headers={pickSchedHeaders(
              `${selectedDate.scheduleDay} ${selectedDate.withoutYear}`,
            )}
            data={csvData}
            filename={`${generateWeekEndString(week.weekdays[6])}_${
              selectedDate.scheduleDay
            }_pick_schedule`}
            onClick={() => {
              // Stop component logic if any data is being retrieved
              if (dataLoadingCount) return false;
              setCsvData(
                formatCsvData(
                  {
                    scheduledTotal: summaryData?.scheduledTotal || 0,
                    pickedTotal: summaryData?.pickedTotal || 0,
                  },
                  pickSchedules,
                ),
              );
              return true;
            }}
            isLoading={!!dataLoadingCount}
          />
        </TitleAndCsvBtn>
        <WeekRow>
          {!isMobile ? (
            <>
              <h3>{week.monday.format('YYYY')} |</h3>
              <TimeframeSwitcher identifier='pick-schedule' timeframe='week' />
              <h3>| Pick {weekdaysToRange(week.weekdays)}</h3>
            </>
          ) : (
            <div className='mobile-only'>
              <TimeframeSwitcher identifier='pick-schedule' timeframe='week' />
              <h3>
                Pick {weekdaysToRange(week.weekdays)} |{' '}
                {week.monday.format('YYYY')}
              </h3>
            </div>
          )}
        </WeekRow>
        <DailyTotalRow
          isFetching={isSummaryFetching}
          fetchingError={fetchSummaryError}
          scheduledTotal={summaryData?.scheduledTotal}
          pickedTotal={summaryData?.pickedTotal}
        />
        {summaryData && summaryData.scheduledTotal > 0 && (
          <ApprovalRow
            isFetching={isSummaryFetching}
            fetchingError={fetchSummaryError}
            isApproved={summaryData.isApproved}
            summaryId={summaryData.id}
          />
        )}
      </Header>
      <DaySelector date={selectedDate.withoutYear} />
      <WithLoadingOverlay isLoading={isLoading}>
        {packHouses?.map(house => (
          <PackHousePickScheduleTable
            key={house.id}
            packHouse={house}
            selectedDate={selectedDate}
            setDataLoadingCount={setDataLoadingCount}
            setPickSchedules={setPickSchedules}
          />
        ))}
      </WithLoadingOverlay>
    </StyledContainer>
  );
};
